/* Conversations: which messages belong together (step 7, the reader's switch).
 *
 * **Reply headers first.** A message joins the conversation of every message
 * its `In-Reply-To` or `References` names, when that message is in the same
 * mailbox. That is what mail clients use and it is exact.
 *
 * **Then the subject, and only for replies.** A message with no usable reply
 * headers but a reply marker on its subject (`Re:`, `RE:`, `Fwd:`, and the
 * common translations) joins the conversation whose subject, markers removed,
 * is the same. A subject WITHOUT a marker never joins by subject alone:
 * otherwise every monthly "Invoice" would collapse into one conversation.
 *
 * Pure, over summaries; union-find, so a mailbox of a hundred thousand
 * messages groups in one pass.
 */

const MARKER = /^\s*((re|fwd?|aw|wg|sv|vs|antw|tr|rv|r|enc|odp|ynt|ha)\s*(\[\d+\])?\s*:\s*)+/i;

/** The subject with its reply and forward markers removed, for comparing. */
export function baseSubject(subject) {
  return (subject || "").replace(MARKER, "").replace(/\s+/g, " ").trim().toLowerCase();
}

export function isReplySubject(subject) {
  return MARKER.test(subject || "");
}

/**
 * @param {Array} items  summaries by id (holes allowed)
 * @returns {{of: Map<number, object>, all: object[]}}  each conversation:
 *   { key, members (ids oldest first), latest (id), latestDate, count, subject, people, attachments }
 */
export function groupConversations(items) {
  const parent = new Map();
  const find = (id) => {
    let root = id;
    while (parent.get(root) !== root) root = parent.get(root);
    for (let at = id; parent.get(at) !== root;) {
      const next = parent.get(at);
      parent.set(at, root);
      at = next;
    }
    return root;
  };
  const join = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(Math.max(ra, rb), Math.min(ra, rb));
  };

  const byMessageId = new Map();
  for (const item of items) {
    if (!item) continue;
    parent.set(item.id, item.id);
    if (item.messageId && !byMessageId.has(item.messageId)) byMessageId.set(item.messageId, item.id);
  }

  const linked = new Set();
  for (const item of items) {
    if (!item) continue;
    for (const ref of [...(item.references || []), ...(item.inReplyTo || [])]) {
      const other = byMessageId.get(ref);
      if (other !== undefined && other !== item.id) {
        join(item.id, other);
        linked.add(item.id);
      }
    }
  }

  // The subject fallback, for replies the headers could not place.
  const bySubject = new Map();
  for (const item of items) {
    if (!item || isReplySubject(item.subject)) continue;
    const base = baseSubject(item.subject);
    if (base && !bySubject.has(base)) bySubject.set(base, item.id);
  }
  for (const item of items) {
    if (!item || linked.has(item.id) || !isReplySubject(item.subject)) continue;
    const base = baseSubject(item.subject);
    if (!base) continue;
    const original = bySubject.get(base);
    if (original !== undefined) join(item.id, original);
    else bySubject.set(base, item.id); // the first reply stands in for an original the file lacks
  }

  const groups = new Map();
  for (const item of items) {
    if (!item) continue;
    const root = find(item.id);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(item);
  }

  const of = new Map();
  const all = [];
  for (const [key, members] of groups) {
    members.sort((a, b) => (a.date ?? Infinity) - (b.date ?? Infinity) || a.id - b.id);
    const latest = [...members].reverse().find((m) => m.date !== null) ?? members[members.length - 1];
    const first = members[0];

    // People, most recent sender first, once each.
    const people = [];
    for (let i = members.length - 1; i >= 0 && people.length < 4; i--) {
      const name = members[i].fromName || members[i].fromAddress;
      if (name && !people.includes(name)) people.push(name);
    }

    const conversation = {
      key,
      members: members.map((m) => m.id),
      latest: latest.id,
      latestDate: latest.date,
      count: members.length,
      subject: first.subject,
      people,
      attachments: members.some((m) => m.maybeAttachments),
    };

    all.push(conversation);
    for (const member of members) of.set(member.id, conversation);
  }

  return { of, all };
}
